import { useEffect, useState, useRef, useCallback } from "react";import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowLeft, Loader2, Users, Copy, Share2, Trophy, FileText, Sparkles,
  UserPlus, Camera, ThumbsUp, Save, Skull, Lock, Smartphone,
} from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const THEORY_POLL_MS = 7000;

const CrewRoom = () => {
  const { crewToken } = useParams();
  const [crew, setCrew] = useState(null);
  const [shareUrl, setShareUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/night/crew/${crewToken}`);
      setCrew(data.crew);
      setShareUrl(data.share_url);
    } catch (err) {
      if (err?.response?.status === 402) {
        setError("This crew hasn't finished payment yet. Return to the signup page and complete checkout.");
      } else if (err?.response?.status === 404) {
        setError("Crew not found — double-check the link.");
      } else {
        setError("Something went wrong loading the room. Try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [crewToken]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="grain min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#8b0000]" size={32} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="grain min-h-screen flex items-center justify-center">
        <div className="card-noir p-8 max-w-md text-center">
          <Lock size={28} className="mx-auto text-[#a39797] mb-3" />
          <div className="text-[#f4f0ec] mb-3">{error}</div>
          <Link to="/night" className="btn-primary text-sm inline-flex">
            <ArrowLeft size={14} /> Back to Night page
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="grain min-h-screen" data-testid="crew-room-page">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <Link to="/night" className="inline-flex items-center gap-2 text-sm text-[#a39797] hover:text-[#d4af37]">
            <ArrowLeft size={16} /> back to NYE Night
          </Link>
          <LanguageSwitcher compact />
        </div>

        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="font-hand text-[#d4af37] rotate-[-1deg] text-lg mb-1">your private crew room</div>
            <h1 className="font-display text-4xl sm:text-5xl text-[#f4f0ec]">{crew.crew_name}</h1>
            <div className="flex items-center gap-3 mt-2 text-xs text-[#a39797] flex-wrap">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#d4af37] text-[#d4af37]">
                <Trophy size={12} /> {crew.tier_label}
              </span>
              <span className="inline-flex items-center gap-1">
                <Users size={12} /> {crew.member_count}/20 members
              </span>
              {crew.bonus_case_unlocked && (
                <span className="inline-flex items-center gap-1 text-[#4ade80]">
                  <Sparkles size={12} /> Bonus case unlocked
                </span>
              )}
            </div>
          </div>
          <ShareRoom shareUrl={shareUrl} />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left column — theory board (wide) */}
          <div className="lg:col-span-2 space-y-6">
            <TheoryBoard crew={crew} crewToken={crewToken} onReload={load} />
            {crew.bonus_case_unlocked && <BonusCaseCard />}
          </div>

          {/* Right column — members + costume contest */}
          <div className="space-y-6">
            <JoinCrew crewToken={crewToken} onJoined={load} />
            <MembersList members={crew.members} />
            <CostumeContest crew={crew} crewToken={crewToken} onReload={load} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Share ----
const ShareRoom = ({ shareUrl }) => {
  const [showQR, setShowQR] = useState(false);
  return (
    <div className="card-noir p-3 flex items-center gap-2 flex-wrap relative" data-testid="crew-share-row">
      <div className="text-xs text-[#a39797] mr-1">Invite link:</div>
      <div className="font-mono text-xs text-[#f4f0ec] max-w-[200px] truncate" title={shareUrl} translate="no">
        {shareUrl.replace(/^https?:\/\//, "")}
      </div>
      <button
        onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied."); }}
        className="btn-ghost text-xs"
        data-testid="crew-copy-link"
      >
        <Copy size={12} /> Copy
      </button>
      <button
        onClick={() => setShowQR(true)}
        className="btn-ghost text-xs"
        data-testid="crew-qr-btn"
        aria-label="Open on your phone"
      >
        <Smartphone size={12} /> On phone
      </button>
      {navigator.share && (
        <button
          onClick={() => navigator.share({ title: "Join my crew for Mystery Night", url: shareUrl }).catch(() => {})}
          className="btn-ghost text-xs"
          data-testid="crew-share-native"
        >
          <Share2 size={12} /> Share
        </button>
      )}
      {showQR && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6"
          onClick={() => setShowQR(false)}
          data-testid="crew-qr-modal"
        >
          <div
            className="card-noir p-6 max-w-sm text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <Smartphone size={16} className="text-[#d4af37]" />
              <div className="font-display text-xl text-[#f4f0ec]">Open on your phone</div>
            </div>
            <p className="text-xs text-[#a39797] mb-4">
              Scan this with your phone's camera. The crew room will open on mobile — perfect for game night.
            </p>
            <div className="bg-white p-4 rounded-lg inline-block mb-4" data-testid="crew-qr-svg">
              <QRCodeSVG value={shareUrl} size={220} level="M" includeMargin={false} />
            </div>
            <div className="text-[11px] text-[#5a5050] mb-3 break-all font-mono" translate="no">
              {shareUrl}
            </div>
            <button onClick={() => setShowQR(false)} className="btn-primary text-sm" data-testid="crew-qr-close">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---- Theory board — autosave with debounce + poll ----
const TheoryBoard = ({ crew, crewToken, onReload }) => {
  const [text, setText] = useState(crew.theory_board_text || "");
  const [nick, setNick] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(crew.theory_board_updated_at || "");
  const saveTimer = useRef(null);
  const lastSynced = useRef(crew.theory_board_text || "");

  // Debounced save
  const scheduleSave = (next, nickname) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        const { data } = await api.put(`/night/crew/${crewToken}/theory-board`, {
          text: next, author_nickname: nickname || "",
        });
        setSavedAt(data.updated_at);
        lastSynced.current = next;
      } catch (err) {
        toast.error("Theory board couldn't save. Will retry.");
      } finally {
        setSaving(false);
      }
    }, 900);
  };

  const onChange = (v) => {
    setText(v);
    scheduleSave(v, nick);
  };

  // Poll for updates every 7s. If someone else edited and the local text
  // hasn't diverged from the last synced snapshot, pull their changes.
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/night/crew/${crewToken}`);
        const remote = data.crew.theory_board_text || "";
        if (remote !== lastSynced.current && text === lastSynced.current) {
          setText(remote);
          lastSynced.current = remote;
          setSavedAt(data.crew.theory_board_updated_at || "");
        }
      } catch (err) { /* ignore */ }
    }, THEORY_POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, crewToken]);

  return (
    <div className="card-noir p-5" data-testid="theory-board">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <FileText size={14} className="text-[#d4af37]" />
            <div className="font-display text-xl text-[#f4f0ec]">Theory Board</div>
          </div>
          <div className="text-xs text-[#a39797]">
            Everyone in the crew can type here. Autosaves. Updates every 7 seconds.
          </div>
        </div>
        <div className="text-[11px] text-[#a39797]">
          {saving ? <span className="inline-flex items-center gap-1"><Loader2 size={10} className="animate-spin" />saving…</span>
            : savedAt ? <span className="inline-flex items-center gap-1"><Save size={10} />saved</span>
            : null}
        </div>
      </div>
      <input
        value={nick}
        onChange={(e) => setNick(e.target.value)}
        placeholder="Your name (shown as last editor)"
        className="w-full bg-[#0a0908] border border-[#2d2424] rounded-md px-3 py-2 text-[#f4f0ec] text-xs mb-2 focus:outline-none focus:border-[#d4af37]"
        data-testid="theory-board-nick"
      />
      <textarea
        value={text}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Jot every theory, suspect, weapon, weird clue…\n\nExample:\n- The butler lied about the wine.\n- Alice was alone from 9:00–9:12.\n- Check the mantle photo.`}
        className="w-full bg-[#0a0908] border border-[#2d2424] rounded-md p-3 text-[#f4f0ec] text-sm min-h-[260px] font-mono leading-relaxed focus:outline-none focus:border-[#d4af37]"
        maxLength={4000}
        data-testid="theory-board-textarea"
      />
      <div className="text-[11px] text-[#5a5050] mt-1">
        {text.length}/4000 · Last edited by {crew.theory_board_author || "nobody yet"}
      </div>
    </div>
  );
};

// ---- Bonus case (premium/deluxe) ----
const BonusCaseCard = () => (
  <div className="card-noir p-5 relative overflow-hidden" data-testid="bonus-case-card">
    <div className="absolute inset-0 bg-gradient-to-br from-[#d4af37]/10 to-transparent pointer-events-none" />
    <div className="relative">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={14} className="text-[#d4af37]" />
        <div className="font-display text-xl text-[#f4f0ec]">Your Bonus Mini-Case</div>
      </div>
      <p className="text-sm text-[#a39797] mb-3">
        Unlocked just for your crew. A 15-minute mystery you can play before (or after) the main event.
        Dropped into your room on event week.
      </p>
      <div className="text-xs text-[#5a5050]">
        We'll email the whole crew when the bonus case drops. No action needed from you.
      </div>
    </div>
  </div>
);

// ---- Join flow ----
const JoinCrew = ({ crewToken, onJoined }) => {
  const [email, setEmail] = useState("");
  const [nickname, setNickname] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const join = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/night/crew/${crewToken}/join`, { email, nickname });
      if (data.status === "already_member") toast.info("Already in the crew.");
      else toast.success("Added to the crew.");
      setEmail(""); setNickname("");
      onJoined();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add that member.");
    } finally { setBusy(false); }
  };

  return (
    <div className="card-noir p-5" data-testid="crew-join-card">
      <div className="flex items-center gap-2 mb-3">
        <UserPlus size={14} className="text-[#d4af37]" />
        <div className="font-display text-lg text-[#f4f0ec]">Add a crewmate</div>
      </div>
      {!open ? (
        <button onClick={() => setOpen(true)} className="btn-primary text-sm w-full justify-center"
          data-testid="crew-add-member-btn">
          <UserPlus size={14} /> Add by email
        </button>
      ) : (
        <form onSubmit={join} className="space-y-2">
          <input type="email" required placeholder="friend@email.com"
            value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-[#0a0908] border border-[#2d2424] rounded-md px-3 py-2 text-[#f4f0ec] text-sm focus:outline-none focus:border-[#d4af37]"
            data-testid="crew-join-email" />
          <input placeholder="Nickname (optional)"
            value={nickname} onChange={(e) => setNickname(e.target.value)}
            className="w-full bg-[#0a0908] border border-[#2d2424] rounded-md px-3 py-2 text-[#f4f0ec] text-sm focus:outline-none focus:border-[#d4af37]"
            data-testid="crew-join-nickname" />
          <button type="submit" disabled={busy} className="btn-primary text-sm w-full justify-center"
            data-testid="crew-join-submit">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <UserPlus size={12} />}
            Add to crew
          </button>
        </form>
      )}
      <div className="text-[11px] text-[#5a5050] mt-2">
        Or just share the invite link — anyone with it can join themselves.
      </div>
    </div>
  );
};

// ---- Members ----
const MembersList = ({ members }) => (
  <div className="card-noir p-5" data-testid="crew-members">
    <div className="flex items-center gap-2 mb-3">
      <Users size={14} className="text-[#d4af37]" />
      <div className="font-display text-lg text-[#f4f0ec]">Your crew</div>
    </div>
    <div className="space-y-2">
      {members.map((m, i) => (
        <div key={i} className="flex items-center justify-between text-sm" data-testid={`crew-member-${i}`}>
          <div className="text-[#f4f0ec] truncate">
            {m.nickname} {m.is_owner && <span className="text-[#d4af37] text-[10px] ml-1">CAPTAIN</span>}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ---- Costume contest ----
const CostumeContest = ({ crew, crewToken, onReload }) => {
  const [nickname, setNickname] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!nickname) return;
    setBusy(true);
    try {
      await api.post(`/night/crew/${crewToken}/costume-entry`, {
        nickname, image_url: imageUrl, caption,
      });
      toast.success("Costume entered!");
      setNickname(""); setImageUrl(""); setCaption("");
      setOpen(false);
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit.");
    } finally { setBusy(false); }
  };

  const vote = async (entryId) => {
    try {
      await api.post(`/night/crew/${crewToken}/costume-entry/${entryId}/vote`);
      toast.success("Voted!");
      onReload();
    } catch (err) {
      if (err?.response?.status === 429) toast.error("Slow down on the votes!");
      else toast.error("Vote failed.");
    }
  };

  const entries = crew.costume_entries || [];
  const sorted = [...entries].sort((a, b) => (b.votes || 0) - (a.votes || 0));

  return (
    <div className="card-noir p-5" data-testid="costume-contest">
      <div className="flex items-center gap-2 mb-1">
        <Camera size={14} className="text-[#d4af37]" />
        <div className="font-display text-lg text-[#f4f0ec]">Costume Contest</div>
      </div>
      <div className="text-xs text-[#a39797] mb-3">Free add-on. Winner gets bragging rights.</div>

      {!open ? (
        <button onClick={() => setOpen(true)} className="btn-primary text-sm w-full justify-center mb-3"
          data-testid="costume-open-btn">
          <Camera size={12} /> Enter the contest
        </button>
      ) : (
        <form onSubmit={submit} className="space-y-2 mb-4">
          <input required placeholder="Your name"
            value={nickname} onChange={(e) => setNickname(e.target.value)}
            className="w-full bg-[#0a0908] border border-[#2d2424] rounded-md px-3 py-2 text-[#f4f0ec] text-xs focus:outline-none focus:border-[#d4af37]"
            data-testid="costume-nickname" />
          <input placeholder="Image URL (Imgur, Instagram, Dropbox…)"
            value={imageUrl} onChange={(e) => setImageUrl(e.target.value)}
            className="w-full bg-[#0a0908] border border-[#2d2424] rounded-md px-3 py-2 text-[#f4f0ec] text-xs focus:outline-none focus:border-[#d4af37]"
            data-testid="costume-image" />
          <input placeholder="Caption (optional)"
            value={caption} onChange={(e) => setCaption(e.target.value)}
            maxLength={180}
            className="w-full bg-[#0a0908] border border-[#2d2424] rounded-md px-3 py-2 text-[#f4f0ec] text-xs focus:outline-none focus:border-[#d4af37]"
            data-testid="costume-caption" />
          <button type="submit" disabled={busy} className="btn-primary text-sm w-full justify-center"
            data-testid="costume-submit">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
            Submit
          </button>
        </form>
      )}

      {sorted.length === 0 ? (
        <div className="text-xs text-[#5a5050]">No entries yet — be first.</div>
      ) : (
        <div className="space-y-3">
          {sorted.map((e, i) => (
            <div key={e.entry_id} className="border border-[#2d2424] rounded-md p-3" data-testid={`costume-entry-${e.entry_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm text-[#f4f0ec] truncate">
                    {i === 0 && <Trophy size={12} className="inline text-[#d4af37] mr-1" />}
                    {e.nickname}
                  </div>
                  {e.caption && <div className="text-xs text-[#a39797] mt-0.5">{e.caption}</div>}
                  {e.image_url && (
                    <a href={e.image_url} target="_blank" rel="noreferrer"
                      className="text-xs text-[#d4af37] hover:underline mt-1 inline-block truncate max-w-full">
                      View photo →
                    </a>
                  )}
                </div>
                <button onClick={() => vote(e.entry_id)}
                  className="btn-ghost text-xs shrink-0"
                  data-testid={`costume-vote-${e.entry_id}`}>
                  <ThumbsUp size={11} /> {e.votes || 0}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CrewRoom;
